import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Headset, Phone, Send } from 'lucide-react';
import { SupportStatus, UserRole, WS_EVENT, type SupportMessagePayload } from '@kick/types';
import { api, getErrorMessage } from '@/lib/api';
import { usePuede } from '@/lib/permisos';
import { cn } from '@/lib/cn';
import { useEventoSocket } from '@/lib/socket';
import { Boton, Cargando, ErrorCarga, Vacio } from '@/components/ui';
import { useAvisos } from '@/components/avisos';

interface Hilo {
  id: string;
  customerId: string;
  customerName: string;
  customerUsername: string;
  customerPhone: string;
  role: UserRole;
  /** El comercio al que pertenece quien escribe; nulo si es un cliente. */
  merchantName: string | null;
  status: SupportStatus;
  lastMessageAt: string;
  lastMessagePreview: string | null;
  sinLeer: number;
}

interface Mensaje {
  id: string;
  senderId: string;
  senderRole: UserRole;
  body: string;
  createdAt: string;
}

/**
 * Bandeja de soporte.
 *
 * Dos columnas: la lista a la izquierda y la conversación a la derecha, como
 * cualquier cliente de correo. Es la forma que ya conoce quien va a pasar el
 * día aquí, y permite responder sin perder de vista lo que sigue esperando.
 */
export function Soporte() {
  const puede = usePuede();
  const cliente = useQueryClient();
  const avisos = useAvisos();
  const [seleccionado, setSeleccionado] = useState<Hilo | null>(null);
  const [todos, setTodos] = useState(false);
  const [texto, setTexto] = useState('');
  const final = useRef<HTMLDivElement>(null);

  const bandeja = useQuery({
    queryKey: ['soporte-admin', todos],
    queryFn: async () =>
      (await api.get<Hilo[]>(`/admin/soporte?limit=100${todos ? '&todos=true' : ''}`)).data,
    refetchInterval: 60_000,
  });

  const conversacion = useQuery({
    queryKey: ['soporte-admin', 'hilo', seleccionado?.id],
    queryFn: async () =>
      (await api.get<Mensaje[]>(`/admin/soporte/${seleccionado!.id}`)).data,
    enabled: !!seleccionado,
  });

  const responder = useMutation({
    mutationFn: async (body: string) =>
      (await api.post(`/admin/soporte/${seleccionado!.id}`, { body })).data,
    onSuccess: () => {
      setTexto('');
      void cliente.invalidateQueries({ queryKey: ['soporte-admin'] });
    },
    onError: (e) => avisos.error(getErrorMessage(e)),
  });

  // Lo que llega en vivo refresca la lista y la conversación abierta.
  useEventoSocket<SupportMessagePayload>(WS_EVENT.SUPPORT_MESSAGE, () => {
    void cliente.invalidateQueries({ queryKey: ['soporte-admin'] });
  });

  useEffect(() => {
    if (conversacion.data?.length) final.current?.scrollIntoView({ block: 'end' });
  }, [conversacion.data]);

  /**
   * Abrir la conversación ya la marca como leída en el servidor.
   *
   * Lo que falta es que se entere el menú, que tiene su propio número: sin
   * esto la insignia seguiría encendida hasta el siguiente minuto avisando de
   * algo que se está leyendo en ese momento.
   */
  useEffect(() => {
    if (!conversacion.data) return;
    void cliente.invalidateQueries({ queryKey: ['admin', 'soporte-sin-leer'] });
  }, [conversacion.data, cliente]);

  if (bandeja.isLoading) return <Cargando />;
  if (bandeja.isError) {
    return (
      <ErrorCarga
        mensaje={getErrorMessage(bandeja.error)}
        onReintentar={() => void bandeja.refetch()}
      />
    );
  }

  const hilos = bandeja.data ?? [];
  const pendientes = hilos.filter((h) => h.status === SupportStatus.PENDING).length;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl text-kick-dark">Soporte</h1>
          <p className="mt-1 text-sm text-kick-gray">
            {pendientes > 0
              ? `${pendientes} ${pendientes === 1 ? 'conversación espera' : 'conversaciones esperan'} respuesta.`
              : 'Nadie esperando. Todo respondido.'}
          </p>
        </div>
        <button
          onClick={() => setTodos((v) => !v)}
          aria-pressed={todos}
          className={cn(
            'rounded-kick border px-3 py-1.5 text-sm font-semibold transition',
            todos
              ? 'border-kick-red bg-kick-red text-white'
              : 'border-kick-gray/25 text-kick-ink hover:border-kick-gray/50',
          )}
        >
          Ver también las respondidas
        </button>
      </div>

      {hilos.length === 0 ? (
        <div className="mt-6">
          <Vacio
            icono={<Headset className="h-6 w-6" />}
            titulo={todos ? 'Ninguna conversación' : 'Nada pendiente'}
            descripcion={
              todos
                ? 'Nadie ha escrito a soporte todavía.'
                : 'Todas las conversaciones tienen su respuesta.'
            }
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-4 lg:grid-cols-[20rem_1fr]">
          {/* Lista */}
          <div className="max-h-[70vh] overflow-y-auto rounded-kick bg-white">
            {hilos.map((h, i) => (
              <button
                key={h.id}
                onClick={() => setSeleccionado(h)}
                className={cn(
                  'block w-full px-4 py-3 text-left transition hover:bg-kick-light',
                  i > 0 && 'border-t border-kick-gray/10',
                  seleccionado?.id === h.id && 'bg-kick-light',
                )}
              >
                <div className="flex items-center gap-2">
                  {h.status === SupportStatus.PENDING && (
                    <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-kick-red" />
                  )}
                  <p className="min-w-0 flex-1 truncate font-semibold text-kick-ink">
                    {h.merchantName ?? h.customerName}
                    {h.merchantName && (
                      <span className="ml-1.5 rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-violet-800">
                        Comercio
                      </span>
                    )}
                  </p>
                  <span className="shrink-0 text-xs text-kick-gray">
                    {haceCuanto(h.lastMessageAt)}
                  </span>
                </div>
                <p className="mt-0.5 truncate text-sm text-kick-gray">
                  {h.lastMessagePreview ?? 'Sin mensajes'}
                </p>
              </button>
            ))}
          </div>

          {/* Conversación */}
          {seleccionado ? (
            <div className="flex max-h-[70vh] flex-col overflow-hidden rounded-kick bg-white">
              <header className="flex flex-wrap items-center justify-between gap-2 border-b border-kick-gray/15 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-bold text-kick-dark">
                    {seleccionado.merchantName ?? seleccionado.customerName}
                  </p>
                  <p className="text-xs text-kick-gray">
                    {seleccionado.merchantName
                      ? `Comercio · escribe ${seleccionado.customerName} (@${seleccionado.customerUsername})`
                      : `Cliente · @${seleccionado.customerUsername}`}
                  </p>
                </div>
                {/* El teléfono a mano: hay cosas que se resuelven llamando. */}
                <a
                  href={`tel:${seleccionado.customerPhone}`}
                  className="flex items-center gap-1.5 text-sm text-kick-gray underline"
                >
                  <Phone className="h-4 w-4" />
                  {seleccionado.customerPhone}
                </a>
              </header>

              <div className="flex-1 space-y-2 overflow-y-auto bg-kick-light p-4">
                {conversacion.isLoading && (
                  <p className="text-center text-sm text-kick-gray">Cargando…</p>
                )}

                {conversacion.data?.map((m) => {
                  const mio = m.senderRole === UserRole.ADMIN;

                  return (
                    <div key={m.id} className={cn('flex', mio ? 'justify-end' : 'justify-start')}>
                      <div
                        className={cn(
                          'max-w-[75%] rounded-kick px-3 py-2 text-sm',
                          mio ? 'bg-kick-red text-white' : 'bg-white text-kick-ink',
                        )}
                      >
                        <p className="whitespace-pre-wrap">{m.body}</p>
                        <p
                          className={cn(
                            'mt-0.5 text-[11px]',
                            mio ? 'text-white/70' : 'text-kick-gray',
                          )}
                        >
                          {new Date(m.createdAt).toLocaleString('es-CO', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'America/Bogota',
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}

                <div ref={final} />
              </div>

              {!puede('soporte', 'responder') ? (
                <p className="border-t border-kick-gray/15 p-3 text-center text-xs text-kick-gray">
                  Tu cuenta puede leer el soporte, pero no responder.
                </p>
              ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (texto.trim()) responder.mutate(texto.trim());
                }}
                className="flex gap-2 border-t border-kick-gray/15 p-3"
              >
                <input
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  maxLength={1000}
                  placeholder="Escribe tu respuesta…"
                  aria-label="Respuesta"
                  className="min-w-0 flex-1 rounded-kick border border-kick-gray/30 px-3 py-2.5 text-sm text-kick-ink outline-none transition focus:border-kick-red"
                />
                <Boton
                  type="submit"
                  className="px-3"
                  disabled={!texto.trim()}
                  cargando={responder.isPending}
                  icono={<Send className="h-4 w-4" />}
                >
                  <span className="sr-only">Enviar</span>
                </Boton>
              </form>
              )}
            </div>
          ) : (
            <div className="grid place-items-center rounded-kick bg-white p-12 text-center">
              <div>
                <Headset className="mx-auto h-10 w-10 text-kick-gray/30" />
                <p className="mt-3 text-sm text-kick-gray">
                  Elige una conversación de la lista para responderla.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function haceCuanto(iso: string): string {
  const minutos = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutos < 1) return 'ahora';
  if (minutos < 60) return `${minutos} min`;
  if (minutos < 60 * 24) return `${Math.floor(minutos / 60)} h`;
  return new Date(iso).toLocaleDateString('es-CO', {
    day: 'numeric',
    month: 'short',
    timeZone: 'America/Bogota',
  });
}
